import React, { ReactNode, RefObject, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import FormInput from '../../FormInput/FormInput';
import PreviewResultPopUp from './PreviewResultPopUp/PreviewResultPopUp';

import { Namespace } from '../../../../../../translation/namespace';
import usePopupKeyboardNavigation, { Selected } from './hooks/usePopupKeyboardNavigation';
import { IEntity } from '../../../../../../types/api/entities/IEntity';
import { Section } from './types/Section';
import { sendLiveRegionMessage } from '../../../../../atoms/LiveRegionGlobal/LiveRegionGlobal';

export type SelectionHandler<T> = (item: Selected<T>) => void;
export type ComboBoxFixedValueProps = {
  id: string;
  labelTitle: string;
  modifierInput?: string;
  modifierListBox?: string;
  inputInContainer?: boolean;
  labelHidden?: boolean;
  hideLabelAddition?: boolean;
  labelHelpText?: string;
  resetButton?: boolean;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  accessKey?: string;
};

export type FormsComboBoxFixedValueProps<TResults extends IEntity> = ComboBoxFixedValueProps & {
  ariaAutoComplete?: 'inline' | 'list' | 'both';
  inputValue?: string;
  // Event Handler
  onSelection: SelectionHandler<TResults>;
  results: Section<TResults>[] | undefined;
  onInput: React.ChangeEventHandler<HTMLInputElement>;
  resultsOpen: boolean;
  openResults: () => void;
  closeResults: () => void;
  formInputRef: RefObject<HTMLInputElement | null>;
  clearInput: () => void;
};

/**
 * Abstract Combobox for fixed value models where we do not use suggestUrl
 */
export function AbstractFormsComboBoxFixedValue<TResults extends IEntity>({
  results,
  accessKey,
  ariaAutoComplete,
  hideLabelAddition,
  id,
  inputInContainer,
  labelHelpText,
  labelHidden,
  labelTitle,
  maxLength,
  modifierInput,
  modifierListBox,
  placeholder,
  required,
  resetButton,
  children,
  onSelection,
  // from useInput
  inputValue,
  formInputRef,
  onInput,
  resultsOpen,
  openResults,
  closeResults,
  clearInput,
}: FormsComboBoxFixedValueProps<TResults> & { children: (item?: TResults) => ReactNode }) {
  const { t } = useTranslation(Namespace.FormsComboBox);

  const allResults = results && results.flatMap(r => r.results);

  const resetState = useCallback(() => {
    closeResults();
    clearInput();
  }, [closeResults, clearInput]);

  const { onKeyDown, markedItem } = usePopupKeyboardNavigation(
    allResults || [],
    onSelection,
    closeResults,
    openResults,
    resetState,
    resultsOpen,
    inputValue,
  );

  // only close if blur is not on popup items -> required for scrollbar usage
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const role = event.relatedTarget?.getAttribute('role');
    if (role !== 'option' && role !== 'listbox') {
      closeResults();
    }
  };

  useEffect(() => {
    if (inputValue) {
      openResults();
    }
  }, [inputValue, openResults]);

  return (
    <div className="combobox">
      {/* if used correctly and wisely, accesskeys are a11y enhancement */}
      {/* eslint-disable-next-line jsx-a11y/no-access-key */}
      <FormInput
        formInputRef={formInputRef}
        id={id}
        value={inputValue}
        ariaRequired={required}
        hideLabelAddition={hideLabelAddition}
        inputInContainer={inputInContainer}
        labelHidden={labelHidden}
        labelHelpText={labelHelpText}
        labelTitle={labelTitle}
        labelId={`combobox-label-${id}`}
        modifierInput={modifierInput}
        autoComplete="off"
        placeholder={placeholder || null}
        resetButton={resetButton}
        // Eventhandler
        onInput={onInput}
        onKeyDown={onKeyDown}
        onMouseDown={openResults}
        onBlur={onBlur}
        onFocus={() => {
          if (allResults && allResults.length > 0) {
            sendLiveRegionMessage(`${allResults.length} ${t('resultsAvailable')}`);
          } else if (allResults) {
            sendLiveRegionMessage(t('noResults'));
          }
        }}
        // Accessibillity
        ariaRole="combobox"
        ariaControls={`combobox-listbox-${id}`}
        ariaAutoComplete={ariaAutoComplete}
        ariaOwns={`combobox-listbox-${id}`}
        ariaHasPopUp="listbox"
        ariaExpanded={resultsOpen}
        accessKey={accessKey}
        maxLength={maxLength}
      />
      <PreviewResultPopUp
        selectedItem={markedItem}
        id={id}
        hidden={!resultsOpen}
        loading={false}
        sections={results || []}
        modifier={modifierListBox}
        formInputRef={formInputRef}
        onSelection={onSelection}
        onBlur={closeResults}
      >
        {children}
      </PreviewResultPopUp>
    </div>
  );
}
